import PropTypes from 'prop-types';
import ModelViewer from '@encodium/rp-ui/components/model-viewer';

import ProductMedia3dModelError, {
	productMedia3dModelErrorClassNamesPropType,
} from './ProductMedia3dModelError.jsx';
import { useProductMedia3dViewerMount } from '../hooks/use-product-media-3d-viewer-mount.js';
import { HERO_3D_MODEL_MIN_HEIGHT_PX } from '../utils/product-media-3d-viewer-mount.js';
import { modelViewerTranslate } from '../utils/product-media-3d-viewer.js';

const modelViewerLabelsPropType = PropTypes.shape({
	loadingModel: PropTypes.string,
	modelViewerAlt: PropTypes.string,
	unableToLoadModel: PropTypes.string,
});

const productMedia3dModelViewerClassNamesPropType = PropTypes.shape({
	mount: PropTypes.string.isRequired,
	mountError: PropTypes.string.isRequired,
});

/**
 * @param {import('prop-types').InferProps<typeof productMedia3dModelViewerPropTypes>} props
 */
export default function ProductMedia3dModelViewer({
	classNames,
	errorClassNames,
	labels = {},
	modelUrl = '',
	viewerHeight,
}) {
	const { hasViewerLoadError, normalizedModelUrl, viewerContainerRef } = useProductMedia3dViewerMount(modelUrl);

	if (normalizedModelUrl === '') {
		return null;
	}

	const resolvedMinHeight = typeof viewerHeight === 'number' && viewerHeight > 0
		? viewerHeight
		: HERO_3D_MODEL_MIN_HEIGHT_PX;
	const viewerClassName = hasViewerLoadError ? classNames.mountError : classNames.mount;

	return (
		<div className={viewerClassName} ref={viewerContainerRef}>
			<ModelViewer
				deferModelLoad={false}
				interactive
				minHeight={resolvedMinHeight}
				src={normalizedModelUrl}
				testId="product-model-viewer"
				translate={(key) => modelViewerTranslate(labels, key)}
			/>
			{hasViewerLoadError && (
				<ProductMedia3dModelError
					classNames={errorClassNames}
					message={modelViewerTranslate(labels, 'unableToLoadModel')}
				/>
			)}
		</div>
	);
}

export const productMedia3dModelViewerPropTypes = {
	classNames: productMedia3dModelViewerClassNamesPropType.isRequired,
	errorClassNames: productMedia3dModelErrorClassNamesPropType.isRequired,
	labels: modelViewerLabelsPropType,
	modelUrl: PropTypes.string,
	viewerHeight: PropTypes.number,
};

ProductMedia3dModelViewer.displayName = 'ProductMedia3dModelViewer';

ProductMedia3dModelViewer.propTypes = productMedia3dModelViewerPropTypes;
